"use client";
import React, { Dispatch, SetStateAction } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { motion } from "framer-motion";
import styled from "styled-components";
import {
  CaptionText,
  LittleSubHeadline,
  LittleText,
  NotificationText,
} from "../typography";
import { COLORS } from "../../assets/utils/enums/colors";
import Dropdown from "./dropdown";
import { DropdownTypeEnum } from "../../assets/utils/enums/table";
import { FinanceTypeTable } from "../../assets/utils/enums/finance";
import helpIcon from '../../assets/images/icons/circle-info-icon.svg';

export type FinanceDataItem = {
  month: string;
  current: number;
  previous: number;
};

export interface FinanceChartProps {
  year?: number;
  currency?: string;
  data?: FinanceDataItem[];
  typeTable?: FinanceTypeTable;
  isDropdown?: boolean;
  typeStat?: string;
  setTypeStat?: (val: string) => void;
}

const monthNames = [
  "Січень",
  "Лютий",
  "Березень",
  "Квітень",
  "Травень",
  "Червень",
  "Липень",
  "Серпень",
  "Вересень",
  "Жовтень",
  "Листопад",
  "Грудень",
];

export default function FinanceChart({
  year = 2025,
  currency = "UAH",
  data,
  typeTable = FinanceTypeTable.EXPENSE,
  isDropdown = false,
  typeStat,
  setTypeStat,
}: FinanceChartProps) {
  const fullData = data ?? [];
  const total = (fullData ?? []).reduce((acc, m) => acc + (m.current ?? 0), 0);

  const hasData = fullData.some((m) => m.current > 0);

  const firstFilled = fullData.find((m) => m.current > 0);
  const lastFilled = [...fullData].reverse().find((m) => m.current > 0);

  const periodText = !hasData
    ? "Немає даних"
    : firstFilled?.month === lastFilled?.month
      ? `за ${monthNames[parseInt(firstFilled!.month) - 1]}`
      : `за ${monthNames[parseInt(firstFilled!.month) - 1]} – ${monthNames[parseInt(lastFilled!.month) - 1]
      }`;

  return (
    <Wrapper>
      <Header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className={`d-flex align-items-center ${isDropdown ? "gap-0" : "gap-3"}`}>
          {isDropdown ? (
            <Dropdown
              type={DropdownTypeEnum.FINANCE_TYPE}
              checkedValue={typeStat!}
              setChecked={setTypeStat! as Dispatch<SetStateAction<string>>}
            />
          ) : (
            <LittleSubHeadline>{typeTable}</LittleSubHeadline>
          )}
          <img
            src={helpIcon}
            className="help-icon"
            alt="help-icon"
            data-tooltip-id="info-tooltip" data-tooltip-content="Діаграма проведених та запланованих транзакцій"
          />
        </div>

        <CaptionText>
          {total.toLocaleString()} {currency}
        </CaptionText>
        <LittleSubHeadline className="gray-color">
          Аналітика {typeStat?.toLocaleLowerCase()} {periodText}, {year}
        </LittleSubHeadline>
      </Header>

      {hasData ? (
        <ResponsiveContainer height={"100%"}>
          <BarChart
            data={fullData}
            margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.gray} />
            <XAxis
              dataKey="month"
              tickFormatter={(m) => m}
              tick={{ fill: COLORS["bg-text-color"], fontSize: 12 }}
            />
            {/* <YAxis
              tick={{ fill: COLORS.text, fontSize: 12 }}
              tickFormatter={(v) => v.toLocaleString()}
            /> */}
            <Tooltip
              cursor={{ fill: COLORS["light-blue"] }}
              content={<CustomTooltip />}
            />

            <Legend />
            <Bar
              dataKey="previous"
              name="Минулого року"
              fill={COLORS.gray}
              radius={[4, 4, 0, 0]}
              barSize={30}
            />
            <Bar
              dataKey="current"
              name="Цього року"
              fill={COLORS.blue}
              radius={[4, 4, 0, 0]}
              barSize={30}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Даних поки немає. Додай надходження, щоб побачити динаміку!
          </motion.p>
        </EmptyState>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  background: ${COLORS.white};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 4px rgba(9, 21, 64, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 60% !important;
  width: -webkit-fill-available;
  font-family: "NunitoSans";

  [data-type="dropdown-label"] {
    padding-left: 0 !important;
  }

  .recharts-cartesian-axis-tick-line,
  .recharts-cartesian-grid-vertical {
    display: none;
  }

  .legend-item-0 span {
    color: ${COLORS["bg-text-color"]} !important;
  }

  .recharts-legend-item .recharts-surface path {
    d: path("M16,0 a16,16 0 1,0 0.00001,0"); /* коло */
    border-radius: 50%;
  }

  .recharts-text tspan {
    ${LittleText.componentStyle.rules.join("")};
    color: ${COLORS["bg-text-color"]};
  }

  .recharts-bar-rectangle path:hover {
    fill-opacity: 1 !important;
  }

  .recharts-legend-wrapper {
    padding-top: 20px;
  }

  .recharts-responsive-container {
    height: 100% !important;
  }

  .recharts-surface {
    outline: none;
  }

  .recharts-default-legend {
    display: flex;
    gap: 16px;

    li {
      margin: 0;
      svg {
        margin-bottom: 2px;
        margin-right: 8px !important;
      }
    }
  }
`;

const Header = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* 
  h3 {
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.secondary1};
  } */

  p {
    /* font-size: 14px; */
    color: ${COLORS.secondary1};
  }

  ${CaptionText} {
    padding-bottom: 12px;
  }

  .gray-color {
    color: ${COLORS["bg-text-color"]};
    font-size: 14px;
  }
`;

export const EmptyState = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${COLORS.secondary1};
  font-size: 15px;
  opacity: 0.7;
`;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <CustomTooltipSt>
      <LittleSubHeadline>{monthNames[parseInt(label) - 1]}</LittleSubHeadline>
      {payload.map((entry: any, i: number) => (
        <div
          key={i}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: "#555",
            fontSize: 12,
            marginTop: 3,
          }}
        >
          <NotificationText style={{ color: entry.color, fontWeight: 500 }}>
            ●{" "}
            <span
              style={{
                color:
                  entry.dataKey === "previous"
                    ? COLORS["bg-text-color"]
                    : entry.color,
              }}
            >
              {entry.name}
            </span>
          </NotificationText>
          <NotificationText
            style={{ fontWeight: 600, color: COLORS.secondary1 }}
          >
            {entry.value.toLocaleString()} UAH
          </NotificationText>
        </div>
      ))}
    </CustomTooltipSt>
  );
};

const CustomTooltipSt = styled.div`
  background: ${COLORS.white};
  padding: 16px;
  border-radius: 10px;
  border: 1px solid ${COLORS.gray};
  box-shadow: 0 1px 4px rgba(9, 21, 64, 0.05);
  display: flex;
  flex-direction: column;
  /* gap: 8px; */
  ${LittleSubHeadline} {
    color: ${COLORS.secondary1};
  }

  div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: ${COLORS["bg-text-color"]};
    gap: 8px;
  }
`;
